/* eslint-disable */
import React, { useState } from 'react';
import {
  Table as MuiTable,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import UIKitIcon from '../UIKITComponents/Icon';
import UIKitIconButton from './IconButton';
import { UIKitCheckbox } from './CheckBox';

import { spacingPx } from '../theme/tokens.spacing';
import { radius } from '../theme/tokens.radius';
import { colors } from '../theme/tokens.colors';
import { shadows } from '../theme/tokens.shadows';
import { uikitPalette } from '../theme/uikitPalette';
import uikitScrollStyle from '../theme/useUIKitScrollStyles';

import { toStr, capitalize } from '../misc/utils';
import EmptyTableMessage from '../components/EmptyTableMessage';
// ======================
// Tipos públicos
// ======================

export interface SortData {
  sortDescending: boolean;
  sortField: string;
}

export interface Cell<T> {
  name?: Extract<keyof T, string>;
  value?: (obj: T) => string | number | null;
  column?: string;
  width?: string | number;
  render?: (value: string, object: T, tdId: string) => React.JSX.Element;
  css?: string;
  disable?: () => boolean;
  tPrefix?: string;
  sortDisabled?: boolean;
  dependsOn?: Extract<keyof T, string> | null;
  alternativeText?: string;
}

/** Configuración de selección por fila (checkbox) */
export interface RowSelectionProps<T> {
  /** Indica si la fila está seleccionada */
  isRowSelected: (row: T) => boolean;
  /** Click en checkbox de fila */
  onToggleRow: (row: T, selected: boolean) => void;
  /**
   * (Opcional) Click en “select all” del header.
   * El padre decide si aplica a todas las filas, solo las visibles, etc.
   */
  onToggleAll?: (selected: boolean) => void;
  /** (Opcional) Para deshabilitar selección en ciertas filas */
  isRowSelectable?: (row: T) => boolean;
}

interface Props<T> {
  data: T[];
  cells: Cell<T>[];
  keyFunc: (obj: T) => number;
  hide?: string[];
  sortData?: SortData;
  changeSort?: (field: string, sortDesc: boolean) => void;
  expandable?: boolean;
  onRowClick?: (obj: T) => void;
  discapitalize?: boolean;
  wrapperClass?: string;
  emptyNoDesc?: boolean;
  tableHeight?:string | number;
  /** Estilo del header según Figma */
  headerContrast?: 'low' | 'high'; // low = blanco, high = azul
  /** Filas con zebra */
  zebraRows?: boolean;

  /** Decide por fila si se puede expandir (opcional) */
  isRowExpandable?: (row: T) => boolean;
  /** Contenido que se muestra al expandir (opcional) */
  renderExpandedRow?: (row: T) => React.ReactNode;

  /** contenido de footer (totales, paginación, etc.) */
  footerContent?: React.ReactNode;

  /** NUEVO: selección por fila */
  rowSelection?: RowSelectionProps<T>;
}

// ======================
// Estilos
// ======================

/** Card de la tabla (borde, radius, sombra) */
const Container = styled('div')({
  borderRadius: radius.s,
  boxShadow: shadows.small,
  border: `1px solid ${colors.neutral[100]}`,
  backgroundColor: colors.specialColors.white,
  display: 'flex',
  flexDirection: 'column',
});

/** Zona scrollable de la tabla */
const StyledTableContainer = styled(TableContainer, {
  shouldForwardProp: (prop) => prop !== 'roundBottom',
})<{ roundBottom?: boolean }>(({ roundBottom }) => ({
  width: '100%',
  borderRadius: roundBottom
    ? `${radius.s}px`
    : `${radius.s}px ${radius.s}px 0 0`,
  overflow: 'auto',
  ...uikitScrollStyle,
}));

const StyledTable = styled(MuiTable)({
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
});

const HeadRow = styled(TableRow)({
  height: 50,
});

const HeadCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'contrast' && prop !== 'sortable',
})<{ contrast: 'low' | 'high'; sortable?: boolean }>(({ contrast, sortable }) => ({
  padding: `${spacingPx[12]}px ${spacingPx[16]}px`,
  fontWeight: 600,
  fontSize: 13,
  borderBottom: `1px solid ${colors.neutral[100]}`,
  whiteSpace: 'nowrap',
  ...(contrast === 'high'
    ? { backgroundColor: uikitPalette.secondary.main, color: colors.specialColors.white }
    : { backgroundColor: colors.specialColors.white, color: colors.neutral[900] }),
  ...(sortable && { cursor: 'pointer', userSelect: 'none' }),
}));

const SortContent = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
});

const SortIcon = styled(UIKitIcon)({
  marginLeft: spacingPx[4],
  fontSize: 16,
});

const BodyRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'zebra',
})<{ zebra?: boolean }>(({ zebra }) => ({
  height: 50,
  '&:hover': {
    backgroundColor: colors.neutral[100],
  },
  ...(zebra && {
    '&:nth-of-type(odd)': {
      backgroundColor: colors.neutral[50],
    },
  }),
}));

const BodyCell = styled(TableCell)({
  padding: `${spacingPx[12]}px ${spacingPx[16]}px`,
  borderBottom: `1px solid ${colors.neutral[100]}`,
  fontSize: 13,
});

const CellContent = styled('div', {
  shouldForwardProp: (prop) => prop !== 'shrink',
})<{ shrink?: boolean }>(({ shrink }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  ...(shrink && { maxHeight: 32 }),
}));

/** Columna de expand */
const ExpandCell = styled(TableCell)({
  width: 40,
  paddingLeft: spacingPx[12],
  paddingRight: spacingPx[8],
  borderBottom: `1px solid ${colors.neutral[100]}`,
});

/** columna de selección (checkbox) */
const SelectCell = styled(TableCell)({
  width: 48,
  paddingLeft: spacingPx[12],
  paddingRight: spacingPx[8],
  borderBottom: `1px solid ${colors.neutral[100]}`,
});

const DetailCell = styled(TableCell)({
  padding: spacingPx[16],
  borderBottom: `1px solid ${colors.neutral[100]}`,
  backgroundColor: colors.neutral[50],
});

/** footer de la tabla (para totales / paginación) */
const Footer = styled('div')({
  borderTop: `1px solid ${colors.neutral[100]}`,
  padding: spacingPx[12],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  backgroundColor: colors.specialColors.white,
  borderBottomLeftRadius: radius.s,
  borderBottomRightRadius: radius.s,
});

const ARROW_DOWN = 'arrow_drop_down';
const ARROW_UP = 'arrow_drop_up';

// ======================
// Componente principal
// ======================

const UIKitTable = <T extends {}>(props: Props<T>) => {
  const {
    data,
    cells,
    keyFunc,
    hide,
    sortData,
    changeSort,
    expandable = false,
    onRowClick,
    discapitalize,
    wrapperClass,
    emptyNoDesc,
    tableHeight= 'auto',
    headerContrast = 'low',
    zebraRows = false,
    isRowExpandable,
    renderExpandedRow,
    footerContent,
    rowSelection,
  } = props;

  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState(0);

  const sortable = sortData !== undefined && changeSort !== undefined;
  const dependsCells = [...cells];

  const hasRowSelection = !!rowSelection;

  // Solo mostramos columna de expand si:
  // - la tabla es expandable
  // - y además existe contenido a renderizar (renderExpandedRow)
  const showExpandColumn = expandable && !!renderExpandedRow;

  const visibleCells = cells.filter(
    x =>
      (!x.dependsOn && (!hide || !x.name || hide.indexOf(x.name) === -1)) ||
      (x.dependsOn &&
        dependsCells.some(
          d => d.name === x.dependsOn && (!hide || hide.indexOf(d.name || '') === -1)
        ))
  );

  // ---------- Selección (para “select all”) ----------

  const selectableRows = hasRowSelection
    ? data.filter(row =>
        rowSelection!.isRowSelectable
          ? rowSelection!.isRowSelectable(row)
          : true
      )
    : [];

  const allSelected =
    hasRowSelection &&
    selectableRows.length > 0 &&
    selectableRows.every(r => rowSelection!.isRowSelected(r));

  const someSelected =
    hasRowSelection &&
    selectableRows.length > 0 &&
    !allSelected &&
    selectableRows.some(r => rowSelection!.isRowSelected(r));

  // ---------- Header ----------
  const drawHeader = (cell: Cell<T>, i: number) => {
    if (sortData && !cell.sortDisabled && changeSort && cell.name) {
      const cellField = !discapitalize ? capitalize(cell.name) : cell.name;
      const sortDesc =
        sortData.sortField === cellField ? sortData.sortDescending : null;

      const sortNext = sortDesc === null ? false : !sortDesc;
      return (
        <HeadCell
          key={`th-${cell.name}-${i}`}
          contrast={headerContrast}
          sortable
          className={cell.css}
          onClick={() => changeSort(cellField, sortNext)}
        >
          <SortContent>
            {t(cell.column || cell.name)}
            {sortDesc !== null && (
              <SortIcon
                name={sortDesc ? ARROW_DOWN : ARROW_UP}
                size="s"
              />
            )}
          </SortContent>
        </HeadCell>
      );
    }

    const columnName = cell.name ? cell.name.toString() : i;
    return (
      <HeadCell
        key={`th-${columnName}-${i}`}
        contrast={headerContrast}
        className={cell.css}
      >
        <span>{t(cell.column || cell.name || '')}</span>
      </HeadCell>
    );
  };

  // ---------- Celdas ----------

  const drawCell = (
    element: T,
    cell: Cell<T>,
    rowId: number,
    i: number,
    shrink: boolean
  ) => {
    const value = cell.value ? toStr(cell.value(element)) : '';
    const idName = `td-${cell.name ? cell.name.toString() : i}-${i}-${rowId}`;
    const jsx =
      cell.render && value !== null
        ? cell.render(value, element, idName)
        : <>{value}</>;

    return (
      <BodyCell
        id={idName}
        className={cell.css}
        key={idName}
      >
        <CellContent shrink={shrink && !!cell.value}>
          {jsx}
        </CellContent>
      </BodyCell>
    );
  };

  // ---------- Filas (principal + detalle) ----------

  const drawRow = (elem: T) => {
    const rowId = keyFunc(elem);

    // ¿Esta fila puede expandirse?
    const canExpand =
      showExpandColumn && (!isRowExpandable || isRowExpandable(elem));
    const isExpanded = canExpand && expandedId === rowId;

    // Selección
    const canSelect =
      hasRowSelection &&
      (rowSelection!.isRowSelectable
        ? rowSelection!.isRowSelectable(elem)
        : true);

    const isSelected =
      hasRowSelection && rowSelection!.isRowSelected(elem);

    const rows: React.ReactNode[] = [];

    // Fila principal
    rows.push(
      <BodyRow
        key={rowId}
        zebra={zebraRows}
        hover
        onClick={() => onRowClick && onRowClick(elem)}
      >
        {hasRowSelection && (
          <SelectCell>
            <UIKitCheckbox
              checked={!!isSelected}
              disabled={!canSelect}
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                if (!hasRowSelection || !canSelect) return;
                rowSelection!.onToggleRow(elem, !isSelected);
              }}
            />
          </SelectCell>
        )}

        {showExpandColumn && (
          <ExpandCell>
            {canExpand && (
              <UIKitIconButton
                variant="standard"
                iconName={isExpanded ? 'expand_more' : 'chevron_right'}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedId(isExpanded ? 0 : rowId);
                }}
              />
            )}
          </ExpandCell>
        )}

        {visibleCells.map((cell, i) =>
          drawCell(elem, cell, rowId, i, canExpand && !isExpanded)
        )}
      </BodyRow>
    );

    // Fila de detalle (solo si hay contenido y está expandido)
    if (canExpand && isExpanded && renderExpandedRow) {
      rows.push(
        <TableRow key={`${rowId}-detail`}>
          <DetailCell
            colSpan={
              visibleCells.length +
              (showExpandColumn ? 1 : 0) +
              (hasRowSelection ? 1 : 0)
            }
          >
            {renderExpandedRow(elem)}
          </DetailCell>
        </TableRow>
      );
    }

    return rows;
  };

  // ---------- Render principal ----------

  return (
    <>
      <Container className={wrapperClass}>
        <StyledTableContainer
          style={{height: props.tableHeight}}
          roundBottom={!footerContent}
        >
          <StyledTable stickyHeader={true}>
            <colgroup>
            {showExpandColumn && <col style={{ width: 40 }} />}

            {visibleCells.map((cell, i) => (
              <col
                key={i}
                style={cell.width ? { width: cell.width, minWidth: cell.width } : undefined}
              />
            ))}
          </colgroup>
            <TableHead>
              <HeadRow>
                {hasRowSelection && (
                  <HeadCell contrast={headerContrast}>
                    {rowSelection!.onToggleAll && selectableRows.length > 0 && (
                      <UIKitCheckbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        color="primary"
                        colorVariant=  {headerContrast === 'high' ? 'light' : 'main'}
                        onClick={(e) => {
                          e.stopPropagation();
                          rowSelection!.onToggleAll!(!allSelected);
                        }}
                      />
                    )}
                  </HeadCell>
                )}

                {showExpandColumn && (
                  <HeadCell contrast={headerContrast} />
                )}

                {visibleCells.map((cell, i) => drawHeader(cell, i))}
              </HeadRow>
            </TableHead>
            <TableBody>
              {data.map(element => drawRow(element))}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>

        {footerContent && (
          <Footer>
            {footerContent}
          </Footer>
        )}
      </Container>

      {data && data.length === 0 && <EmptyTableMessage noDesc={emptyNoDesc} subtitle={t('noMatchingResultsFound')} />}
    </>
  );
};

export default UIKitTable;
